import { RubyGemMark } from '../../components/RubyGemMark';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { FolderSearch, Plus, RefreshCw, X } from 'lucide-react';
import { CHECKLIST_STATE_LABEL, FieldLabel, IconAlert } from '@documents-ui';
import type { ChecklistRow } from '@documents-ui';
import type {
  DocumentContextType, DocumentPack, LegalDocument, RequestParty, RequestStatus, SignerRole,
} from '@platform/shared-types';
import SearchableDropdown from '../../components/SearchableDropdown';
import { toastService } from '../../services/toastService';
import { documentsApi } from '../../services/documentsApi';
import type { EnvelopeDetail } from '../../services/documentsApi';
import { documentsExtApi } from '../../services/documentsExtApi';
import type { RequestView } from '../../services/documentsExtApi';
import { formatUtcStamp } from './legalFormat';
import { StaffPage } from '../../components/staff/StaffChrome';
import LegalNav from './LegalNav';
import { RubyLoader } from '../../components/RubyLoader';

/**
 * LEGAL / DOCUMENTS HUB — the one place every generated document is visible,
 * tenant-wise, from creation to execution (spec 2026-08-26 §5).
 *
 * Every row is a DOCUMENT REQUEST; its item states arrive LIVE-DERIVED from
 * the envelope rows and the acceptance ledger (the service re-reads them per
 * view), and the request status is computed from the items on every read —
 * nothing on this screen is a stored flag that can drift.
 *
 * Pagination is REAL: `nextToken === null` means the listing is complete.
 * Status/context filters narrow the loaded pages and the note below the
 * table says so — a filtered count never implies a census it did not take.
 */

const CONTEXT_LABEL: Record<DocumentContextType, string> = {
  tenant_onboarding: 'Onboarding',
  location_add: 'New location',
  device_order: 'Device order',
  manual: 'Manual',
  // The four counterparty processes. Without these a vendor agreement and an
  // employment agreement both showed as "Manual" and were indistinguishable
  // in this list — which is the reason the context types exist at all.
  vendor_agreement: 'Vendor',
  employment: 'Employment',
  partner_agreement: 'Partner',
  software_agreement: 'Software',
};

const STATUS_TONE = {
  draft: 'draft', in_progress: 'review', blocked: 'retired', complete: 'active',
} as const;

/* ── RUBY light-chrome primitives (local restyle of the dark @documents-ui
      primitives, which also render in platform-app and must stay dark). ── */

const BTN_BASE = 'inline-flex min-h-[44px] items-center justify-center gap-2 rounded-lg border px-4 text-[13px] transition-colors disabled:cursor-not-allowed disabled:opacity-50';
const BTN = {
  default: `${BTN_BASE} border-stone-300 bg-white text-stone-700 hover:bg-stone-50`,
  primary: `${BTN_BASE} border-transparent bg-admin-primary font-semibold text-white hover:bg-admin-secondary`,
  danger: `${BTN_BASE} border-red-200 bg-white text-red-600 hover:bg-red-50`,
};

const LIGHT_INPUT = 'w-full min-h-[44px] rounded-lg border border-stone-300 bg-white px-3 py-2.5 text-[13px] text-stone-900 placeholder-stone-400 focus:border-admin-primary focus:outline-none focus:ring-1 focus:ring-admin-primary';

const LIGHT_TAG_TONES: Record<string, string> = {
  active: 'bg-emerald-100 text-emerald-800',
  review: 'bg-amber-100 text-amber-800',
  retired: 'bg-red-100 text-red-800',
  draft: 'bg-stone-100 text-stone-600',
};

function LightTag({ tone, children }: { tone: string; children: ReactNode }) {
  return (
    <span className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-[10.5px] font-semibold tracking-wide ${LIGHT_TAG_TONES[tone] ?? LIGHT_TAG_TONES.draft}`}>
      {children}
    </span>
  );
}

function LightWarn({ children }: { children: ReactNode }) {
  return (
    <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs leading-relaxed text-amber-800">
      <IconAlert size={14} className="mt-0.5 shrink-0" />
      <div>{children}</div>
    </div>
  );
}

/** Light modal shell — same structure/behavior as @documents-ui ModalShell, white card. */
function LightModal({
  title, subtitle, children, footerNote, footerActions, onClose, labelledById,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  children: ReactNode;
  footerNote?: ReactNode;
  footerActions?: ReactNode;
  onClose: () => void;
  labelledById?: string;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4 sm:items-center">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledById}
        className="w-full max-w-[620px] rounded-2xl border border-stone-200 bg-white shadow-xl"
      >
        <div className="flex items-start gap-3 border-b border-stone-200 px-5 py-4">
          <div className="flex-1">
            <h3 id={labelledById} className="text-base font-semibold text-stone-900">{title}</h3>
            {subtitle && <p className="mt-1 text-[12.5px] text-stone-500">{subtitle}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="-mr-2 -mt-2 flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-stone-400 hover:bg-stone-100 hover:text-stone-900"
          >
            <X size={18} />
          </button>
        </div>
        <div className="px-5 py-5">{children}</div>
        {(footerNote || footerActions) && (
          <div className="flex flex-col gap-3 border-t border-stone-200 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
            <span className="text-[11.5px] text-stone-500">{footerNote}</span>
            <div className="flex gap-2">{footerActions}</div>
          </div>
        )}
      </div>
    </div>
  );
}

/** Item states → light status tints (envelope lifecycle, spec §6). */
const CHECK_STATE_TINT: Record<ChecklistRow['state'], string> = {
  awaiting_data: 'bg-amber-100 text-amber-800',
  ready: 'bg-blue-100 text-blue-800',
  sent: 'bg-blue-100 text-blue-800',
  partially_signed: 'bg-amber-100 text-amber-800',
  executed: 'bg-emerald-100 text-emerald-800',
  accepted: 'bg-emerald-100 text-emerald-800',
  declined: 'bg-red-100 text-red-800',
  voided: 'bg-red-100 text-red-800',
  expired: 'bg-red-100 text-red-800',
  removed: 'bg-stone-100 text-stone-600',
};

/**
 * Light rendering of @documents-ui's DocumentChecklist (which is dark-surface
 * and shared with platform-app). Same rows, same callbacks — classes only.
 */
function LightChecklist({ rows, footnote }: { rows: ChecklistRow[]; footnote?: ReactNode }) {
  if (!rows.length) {
    return <p className="text-[12.5px] text-stone-500">No documents attached to this process.</p>;
  }
  return (
    <div>
      <ul className="divide-y divide-stone-200 rounded-xl border border-stone-200 bg-white">
        {rows.map((row) => (
          <li key={row.itemId} className={`flex flex-wrap items-center gap-3 px-4 py-3 ${row.state === 'removed' ? 'opacity-50' : ''}`}>
            <div className="min-w-0 flex-1">
              <p className="truncate text-[13px] font-medium text-stone-900">
                {row.title}
                {!row.required && <span className="ml-2 text-[11px] font-normal text-stone-500">optional</span>}
              </p>
              <p className="text-[11.5px] text-stone-500">
                {row.kind === 'agreement' ? 'Agreement — signed' : 'Policy — accepted'}
              </p>
              {row.state === 'awaiting_data' && !!row.missingFields?.length && (
                <p className="mt-1 text-[11.5px] text-amber-700">
                  Missing: {row.missingFields.join(', ')}
                </p>
              )}
              {row.state === 'removed' && row.removedReason && (
                <p className="mt-1 text-[11.5px] text-stone-500">Removed: {row.removedReason}</p>
              )}
            </div>
            <span className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-[10.5px] font-semibold tracking-wide ${CHECK_STATE_TINT[row.state]}`}>
              {CHECKLIST_STATE_LABEL[row.state]}
            </span>
            {row.onSecondary && (
              <button type="button" className={BTN.default} onClick={row.onSecondary}>{row.secondaryLabel ?? 'View'}</button>
            )}
            {row.onPrimary && (
              <button type="button" className={BTN.primary} onClick={row.onPrimary}>{row.primaryLabel ?? 'Open'}</button>
            )}
          </li>
        ))}
      </ul>
      {footnote && <p className="mt-2 text-[11.5px] text-stone-500">{footnote}</p>}
    </div>
  );
}

/**
 * MANUAL REQUEST — an ad-hoc send from the hub (spec §6, context `manual`).
 * The engine does the heavy honesty: incomplete merge data lands items in
 * `awaiting_data` naming the missing fields, and a pack role nobody was named
 * for refuses at create, while the admin is still on this screen.
 */
function NewRequestModal({
  documents, onClose, onCreated,
}: {
  documents: LegalDocument[];
  onClose: () => void;
  onCreated: () => void;
}) {
  const [packs, setPacks] = useState<DocumentPack[]>([]);
  const [tenantId, setTenantId] = useState('');
  const [packId, setPackId] = useState<string | null>(null);
  const [docId, setDocId] = useState<string | null>(null);
  const [parties, setParties] = useState<RequestParty[]>([
    { role: 'tenant_admin' as SignerRole, name: '', email: '' },
  ]);
  const [mergePairs, setMergePairs] = useState<{ k: string; v: string }[]>([{ k: '', v: '' }]);
  const [sendNow, setSendNow] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    documentsExtApi.listPacks()
      .then((p) => setPacks(p.packs.filter((x) => x.status === 'active')))
      .catch(() => setPacks([]));
  }, []);

  const agreementDocs = documents.filter((d) => d.status !== 'retired');

  const canCreate = tenantId.trim().length > 0 && (packId || docId)
    && parties.every((p) => p.name.trim());

  const create = async () => {
    if (!canCreate) return;
    setBusy(true);
    setError(null);
    try {
      const chosenDoc = docId ? documents.find((d) => d.documentId === docId) : null;
      const mergeData = Object.fromEntries(
        mergePairs.filter((p) => p.k.trim()).map((p) => [p.k.trim(), p.v]),
      );
      await documentsExtApi.createRequest({
        tenantId: tenantId.trim(),
        context: { type: 'manual', refId: `manual-${Date.now()}` },
        packId: packId ?? undefined,
        items: packId ? undefined : [{
          documentId: chosenDoc!.documentId,
          kind: chosenDoc!.kind as 'agreement' | 'policy',
          required: true,
          signerRoles: chosenDoc!.kind === 'agreement'
            ? (parties.map((p) => p.role) as SignerRole[])
            : [],
        }],
        parties: parties.map((p) => ({
          role: p.role, name: p.name.trim(), email: p.email?.trim() || undefined,
        })),
        mergeData,
        send: sendNow,
      });
      toastService.success('Request created');
      onCreated();
    } catch (e: any) {
      setError(e?.message || 'request_create_failed');
    } finally {
      setBusy(false);
    }
  };

  const ROLES: SignerRole[] = ['tenant_admin', 'guarantor', 'partner', 'platform'] as SignerRole[];

  return (
    <LightModal
      labelledById="new-request-title"
      title="New document request"
      subtitle="Attach a pack — or one document — to a tenant, name the people who sign, and send. Missing merge fields park items in “awaiting data” rather than failing."
      onClose={onClose}
      footerActions={(
        <>
          <button type="button" className={BTN.default} onClick={onClose} disabled={busy}>Cancel</button>
          <button type="button" className={BTN.primary} disabled={!canCreate || busy} onClick={() => void create()}>
            {busy ? 'Creating…' : sendNow ? 'Create & send' : 'Create draft'}
          </button>
        </>
      )}
    >
      <div className="space-y-4">
        {error && <LightWarn>{error}</LightWarn>}
        <div>
          <FieldLabel>Tenant id</FieldLabel>
          <input
            className={LIGHT_INPUT}
            value={tenantId}
            onChange={(e) => setTenantId(e.target.value)}
            placeholder="tenant_…"
          />
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <SearchableDropdown
            label="Pack"
            value={packId}
            placeholder="Choose a pack…"
            options={packs.map((p) => ({ value: p.packId, label: p.name }))}
            onChange={(v) => { setPackId(v); if (v) setDocId(null); }}
          />
          <SearchableDropdown
            label="…or a single document"
            value={docId}
            placeholder="Choose a document…"
            options={agreementDocs.map((d) => ({ value: d.documentId, label: d.title, subtitle: d.kind }))}
            onChange={(v) => { setDocId(v); if (v) setPackId(null); }}
          />
        </div>

        <div>
          <FieldLabel>Parties (who signs)</FieldLabel>
          <div className="space-y-2">
            {parties.map((p, i) => (
              <div key={i} className="flex flex-wrap items-center gap-2">
                <div className="w-40">
                  <SearchableDropdown
                    compact
                    value={p.role}
                    options={ROLES.map((r) => ({ value: r, label: r }))}
                    onChange={(v) => {
                      if (v) setParties(parties.map((x, j) => (j === i ? { ...x, role: v as SignerRole } : x)));
                    }}
                  />
                </div>
                <input
                  className={`${LIGHT_INPUT} flex-1`}
                  placeholder="Full legal name"
                  value={p.name}
                  onChange={(e) => setParties(parties.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))}
                />
                <input
                  className={`${LIGHT_INPUT} flex-1`}
                  placeholder="email (required for outside parties)"
                  value={p.email ?? ''}
                  onChange={(e) => setParties(parties.map((x, j) => (j === i ? { ...x, email: e.target.value } : x)))}
                />
                <button type="button" className={BTN.danger} onClick={() => setParties(parties.filter((_, j) => j !== i))}>
                  ×
                </button>
              </div>
            ))}
            <button
              type="button"
              className={BTN.default}
              onClick={() => setParties([...parties, { role: 'guarantor' as SignerRole, name: '', email: '' }])}
            >
              + Add party
            </button>
          </div>
        </div>

        <div>
          <FieldLabel>Merge data (field → value; leave blank to fill later)</FieldLabel>
          <div className="space-y-2">
            {mergePairs.map((p, i) => (
              <div key={i} className="flex gap-2">
                <input
                  className={`${LIGHT_INPUT} flex-1`}
                  placeholder="businessName"
                  value={p.k}
                  onChange={(e) => setMergePairs(mergePairs.map((x, j) => (j === i ? { ...x, k: e.target.value } : x)))}
                />
                <input
                  className={`${LIGHT_INPUT} flex-1`}
                  placeholder="Joe's Vape Shop"
                  value={p.v}
                  onChange={(e) => setMergePairs(mergePairs.map((x, j) => (j === i ? { ...x, v: e.target.value } : x)))}
                />
              </div>
            ))}
            <button type="button" className={BTN.default} onClick={() => setMergePairs([...mergePairs, { k: '', v: '' }])}>
              + Add field
            </button>
          </div>
        </div>

        <label className="flex min-h-[44px] cursor-pointer items-center gap-2 text-[12.5px] text-stone-600">
          <input type="checkbox" checked={sendNow} onChange={(e) => setSendNow(e.target.checked)} />
          Send immediately (ready items become envelopes; outside parties get their email)
        </label>
      </div>
    </LightModal>
  );
}

export default function DocumentsHubPage() {
  const [pages, setPages] = useState<RequestView[]>([]);
  const [nextToken, setNextToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [documents, setDocuments] = useState<LegalDocument[]>([]);

  const [tenantFilter, setTenantFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<RequestStatus | ''>('');
  const [contextFilter, setContextFilter] = useState<DocumentContextType | ''>('');

  const [openRequest, setOpenRequest] = useState<RequestView | null>(null);
  const [envelopeDetail, setEnvelopeDetail] = useState<EnvelopeDetail | null>(null);
  const [envelopeLoading, setEnvelopeLoading] = useState(false);
  const [newOpen, setNewOpen] = useState(false);

  const titleById = useMemo(
    () => Object.fromEntries(documents.map((d) => [d.documentId, d.title])),
    [documents],
  );

  const load = useCallback(async (append = false, token?: string) => {
    if (append) setLoadingMore(true); else { setLoading(true); setError(null); }
    try {
      const [page, docs] = await Promise.all([
        documentsExtApi.listRequests({
          tenantId: tenantFilter.trim() || undefined,
          status: (statusFilter || undefined) as RequestStatus | undefined,
          contextType: (contextFilter || undefined) as DocumentContextType | undefined,
          nextToken: token,
        }),
        append || documents.length ? Promise.resolve(null) : documentsApi.listDocuments(),
      ]);
      if (docs) setDocuments(docs.documents || []);
      setPages((prev) => (append ? [...prev, ...page.requests] : page.requests));
      setNextToken(page.nextToken);
    } catch (e: any) {
      if (!append) { setPages([]); setError(e?.message || 'hub_unavailable'); }
      else toastService.error(e?.message || 'load_more_failed');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [tenantFilter, statusFilter, contextFilter, documents.length]);

  useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [statusFilter, contextFilter]);

  const openEnvelope = useCallback(async (envelopeId: string) => {
    setEnvelopeLoading(true);
    setEnvelopeDetail(null);
    try {
      setEnvelopeDetail(await documentsApi.getEnvelope(envelopeId));
    } catch (e: any) {
      toastService.error(e?.message || 'envelope_unavailable');
    } finally {
      setEnvelopeLoading(false);
    }
  }, []);

  const rowsFor = (view: RequestView): ChecklistRow[] =>
    view.items.map((item) => ({
      itemId: item.itemId,
      title: titleById[item.documentId] ?? item.documentId,
      kind: item.kind,
      state: item.state,
      required: item.required,
      missingFields: item.missingFields,
      removedReason: item.removedReason,
      onPrimary: item.envelopeId ? () => void openEnvelope(item.envelopeId!) : undefined,
      primaryLabel: 'Timeline',
    }));

  return (
    <StaffPage
      section="Legal"
      tabs={<LegalNav />}
      icon={FolderSearch}
      title="Documents Hub"
      subtitle="Every document request, tenant-wise, from creation to execution. States are derived live from the envelopes and the acceptance ledger — never stored, never stale."
      actions={(
        <>
          <button type="button" className={BTN.default} onClick={() => void load()} disabled={loading}>
            {loading ? <RubyGemMark size={16} drawing color="currentColor" /> : <RefreshCw className="h-4 w-4" />} Refresh
          </button>
          <button type="button" className={BTN.primary} onClick={() => setNewOpen(true)}>
            <Plus className="h-4 w-4" /> New request
          </button>
        </>
      )}
    >
      <div className="space-y-4">
        {/* Filters */}
        <div className="flex flex-wrap items-end gap-3">
          <div className="w-56">
            <p className="mb-1 text-[11px] uppercase tracking-wider text-stone-500">Tenant id</p>
            <div className="flex gap-2">
              <input
                className={LIGHT_INPUT}
                value={tenantFilter}
                onChange={(e) => setTenantFilter(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') void load(); }}
                placeholder="tenant_…"
              />
              <button type="button" className={BTN.default} onClick={() => void load()}>Go</button>
            </div>
          </div>
          <div className="w-44">
            <SearchableDropdown
              label="Status"
              value={statusFilter || null}
              placeholder="Any status"
              options={(['draft', 'in_progress', 'blocked', 'complete'] as RequestStatus[])
                .map((s) => ({ value: s, label: s.replace('_', ' ') }))}
              onChange={(v) => setStatusFilter((v ?? '') as RequestStatus | '')}
            />
          </div>
          <div className="w-44">
            <SearchableDropdown
              label="Context"
              value={contextFilter || null}
              placeholder="Any context"
              options={(Object.keys(CONTEXT_LABEL) as DocumentContextType[])
                .map((c) => ({ value: c, label: CONTEXT_LABEL[c] }))}
              onChange={(v) => setContextFilter((v ?? '') as DocumentContextType | '')}
            />
          </div>
        </div>

        {loading && (
          <RubyLoader variant="section" label="Loading requests…" />
        )}
        {error && !loading && (
          <LightWarn>The hub could not load: {error}. The documents-ext service may not be deployed to this stage yet.</LightWarn>
        )}
        {!loading && !error && pages.length === 0 && (
          <p className="text-[12.5px] text-stone-500">
            No document requests{tenantFilter ? ` for ${tenantFilter}` : ''} yet. Requests appear here
            the moment a process attaches documents — onboarding, a new location, a financed order,
            or a manual send.
          </p>
        )}

        {!loading && !error && pages.map((view) => (
          <div key={view.request.requestId} className="rounded-xl border border-stone-200 bg-white shadow-sm">
            <button
              type="button"
              className="flex w-full flex-wrap items-center gap-3 px-4 py-3 text-left"
              onClick={() => setOpenRequest(openRequest?.request.requestId === view.request.requestId ? null : view)}
            >
              <div className="min-w-0 flex-1">
                <p className="text-[13px] font-medium text-stone-900">
                  {view.request.tenantId}
                  <span className="ml-2 text-[11.5px] font-normal text-stone-500">
                    {CONTEXT_LABEL[view.request.context.type] ?? view.request.context.type} · {view.request.context.refId}
                  </span>
                </p>
                <p className="text-[11.5px] text-stone-500">
                  {view.items.filter((i) => i.state !== 'removed').length} document{view.items.length === 1 ? '' : 's'}
                  {' · created '}{formatUtcStamp(view.request.createdAt)} · by {view.request.createdBy}
                </p>
              </div>
              <LightTag tone={STATUS_TONE[view.status]}>{view.status.replace('_', ' ')}</LightTag>
            </button>
            {openRequest?.request.requestId === view.request.requestId && (
              <div className="border-t border-stone-200 bg-stone-50 p-4 rounded-b-xl">
                <LightChecklist
                  rows={rowsFor(view)}
                  footnote="States are derived live from the envelope rows and the acceptance ledger."
                />
              </div>
            )}
          </div>
        ))}

        {nextToken && !loading && (
          <button type="button" className={BTN.default} onClick={() => void load(true, nextToken)} disabled={loadingMore}>
            {loadingMore ? 'Loading…' : 'Load more'}
          </button>
        )}
        {!loading && !error && pages.length > 0 && !nextToken && (
          <p className="text-[11.5px] text-stone-500">
            End of listing — every matching request is shown.
            {(statusFilter || contextFilter) && ' Filters narrow the loaded pages.'}
          </p>
        )}
      </div>

      {newOpen && (
        <NewRequestModal
          documents={documents}
          onClose={() => setNewOpen(false)}
          onCreated={() => { setNewOpen(false); void load(); }}
        />
      )}

      {/* Envelope timeline drawer */}
      {(envelopeLoading || envelopeDetail) && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/40" onClick={() => setEnvelopeDetail(null)}>
          <div
            className="h-full w-full max-w-[440px] overflow-y-auto border-l border-stone-200 bg-white p-5 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-stone-900">Envelope timeline</h3>
              <button
                type="button"
                aria-label="Close"
                onClick={() => setEnvelopeDetail(null)}
                className="flex h-11 w-11 items-center justify-center rounded-lg text-stone-400 hover:bg-stone-100 hover:text-stone-900"
              >
                <X size={18} />
              </button>
            </div>
            {envelopeLoading && (
              <RubyLoader variant="section" label="Loading…" />
            )}
            {envelopeDetail && (
              <>
                <p className="mb-1 text-[12.5px] text-stone-700">
                  {envelopeDetail.envelope.templateDocumentId} · v{envelopeDetail.envelope.templateVersionNo}
                </p>
                <p className="mb-4 text-[11.5px] text-stone-500">
                  {envelopeDetail.envelope.envelopeId} · {envelopeDetail.envelope.status}
                </p>
                <h4 className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-stone-500">Signers</h4>
                <ul className="mb-4 space-y-1.5">
                  {envelopeDetail.signers.map((s) => (
                    <li key={s.signerId} className="text-[12.5px] text-stone-700">
                      {s.name} <span className="text-stone-500">({s.role})</span> — {s.status}
                    </li>
                  ))}
                </ul>
                <h4 className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-stone-500">Timeline (UTC)</h4>
                <ol className="space-y-2">
                  {envelopeDetail.events.map((e, i) => (
                    <li key={`${e.at}-${i}`} className="text-[12px] text-stone-700">
                      <span className="text-stone-500">{formatUtcStamp(e.at ?? undefined)}</span> — {e.action}
                    </li>
                  ))}
                </ol>
              </>
            )}
          </div>
        </div>
      )}
    </StaffPage>
  );
}
